import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { ComponentToLoad, Game, GameSettings, GameState, GameStateId, GameToLoad, TableMessage } from './game.state';
import { Observable, Subscription, of, firstValueFrom, merge, combineLatest } from 'rxjs';
import * as GameActions  from "./game.actions";
import { Firestore, doc, onSnapshot, collectionData, docData, Unsubscribe } from '@angular/fire/firestore';
import { filter, map, pluck, switchMap, distinctUntilChanged, withLatestFrom, takeUntil } from 'rxjs/operators';
import { ProfileStateDispatcher } from '../profile/profile.dispatcher';
import { SmartAudioService, Sound } from 'src/app/shared/services/smart-audio/smart-audio.service';
import { GameType, GameEventType, IGameState, LimitType, TableType, ITableState, ITournamentState, PlayerID, IStrippedGameState, CurrencyType, IPlayerDictionary } from '@soposopo/sopo-models';
import * as GameSettingsAction from '../gameSettings/gameSettings.actions';
import { FireSubService } from '../../firebase/fireSub.service';
import { GameService } from '../../firebase/game.service';
import * as EmotesActions from '../emotes/emotes.actions';
import { NavigationEnd, Router } from '@angular/router';
import { isElectron } from 'src/app/shared/utils/platform.utils';
import { GameTabManager } from 'src/app/shared/services/game-tab-manager/game-tab-manager.interface';

@Injectable()
export class GameStoreDispatcher {
    private tableSubscription: { [key: string]: Subscription} = {};
    private detachTableMessageListener: { [key: string]: Unsubscribe} = {};
    private tournamentSubscription: { [key: string]: Subscription} = {};
    private audioSubscription: { [key: string]: Subscription} = {};
    private timebankSubscription: Subscription;
    private isInGameSubscription: Subscription;
    private isInGame = false;
    private isOpeningGames: string[] = [];

    constructor(
        private store: Store<AppState>,
        private firestore: Firestore,
        private audioService: SmartAudioService,
        private ProfileStore: ProfileStateDispatcher,
        private fireSubService: FireSubService,
        private gameService: GameService,
        private router: Router,
       private injector: Injector,
    ) {
        const locallyStoredGameID = localStorage.getItem('gameID');
        if (locallyStoredGameID) {
            this.store.dispatch(GameActions.GameDispatcherActions.gameIdLoadedFromLocalStorage({id: locallyStoredGameID}));
        }
        this.isInGameSubscription = this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            if(event.url.includes('game')) this.isInGame = true;
            else this.isInGame = false;
        });
    }

    public setSelectedGame(index: GameStateId) {
        this.store.dispatch(GameActions.GameDispatcherActions.changeSelectedGame({gameStateId: index}));
    }

    public setGridView(enabled: boolean) {
        this.store.dispatch(GameActions.GameDispatcherActions.setGridView({enabled}));
    }

    public addNewGame(game: ComponentToLoad, toNav: boolean = false, isInit: boolean = false, settings?: GameSettings, promptOwner?: boolean): Promise<ComponentToLoad> {
        if (this.isOpeningGames.includes(game.gameStateId)) return Promise.resolve(null);
        this.isOpeningGames.push(game.gameStateId);

        return new Promise((resolve, reject) => {
            let openedChannel: BroadcastChannel | null = null;
            const timeout = setTimeout(() => {
                if (openedChannel) { openedChannel.close(); openedChannel = null; }
                this.isOpeningGames = this.isOpeningGames.filter(id => id !== game.gameStateId);
                resolve(game);
            }, 5000);

            this.setSelectedGame(game.gameStateId);

            const gameTabName = game.componentType === 'game'
                ? (game.tournamentId ? `gameTab-${game.tournamentId}-${game.gameStateId}` : `gameTab-${game.gameStateId}`)
                : null;

            const setTabFocus = (name: string) => {
                const oldName = this.isInGame ? window.name : undefined;

                if (isElectron()) {
                    const manager = this.injector.get(GameTabManager);
                    if (manager && typeof manager.openOrFocus === 'function') {
                        manager.openOrFocus(name, oldName).then(() => {
                            clearTimeout(timeout);
                            this.isOpeningGames = this.isOpeningGames.filter(id => id !== game.gameStateId);
                            resolve(game);
                        });
                        return;
                    }
                }
                const channel = new BroadcastChannel('gameTab-manager');
                openedChannel = channel;
                channel.postMessage({ type: 'OPEN_OR_FOCUS_TABLE', gameTabName: name, oldGameTabName: oldName });
                channel.onmessage = (event) => {
                    if (event.data.type === 'TAB_OPENED' && event.data.gameTabName === name) {
                        clearTimeout(timeout);
                        channel.close();
                        this.isOpeningGames = this.isOpeningGames.filter(id => id !== game.gameStateId);
                        resolve(game);
                    }
                };
            };

            if (gameTabName) {
                setTabFocus(gameTabName);
            } else {
                clearTimeout(timeout);
                this.isOpeningGames = this.isOpeningGames.filter(id => id !== game.gameStateId);
                resolve(game);
            }
        });
    }

    public addGameToState(game: ComponentToLoad) {
        this.store.dispatch(GameActions.GameDispatcherActions.addNewGameToState(game, true));
    }

    public async removeGame(gameStateId: GameStateId, currency?: CurrencyType, invitationRejected?: boolean) {
        this.leaveGame(gameStateId);
        this.store.dispatch(GameActions.GameDispatcherActions.removeGameFromGamesList({gameStateId, currency, invitationRejected}));
    }

    public clearGamesList(){
        this.store.dispatch(GameActions.GameDispatcherActions.clearGamesList());
    }

    public modifyGameSettings(gameStateId: GameStateId, settings: GameSettings) {
        this.store.dispatch(GameActions.GameDispatcherActions.modifyGameSettings({settings, gameStateId}));
    }

    /** Load table messages */
    public async loadTableMessages(gameStateId: GameStateId, gameID: string, playerId: string) {
        if (this.detachTableMessageListener[gameStateId]) {
            this.detachTableMessageListener[gameStateId]();
        }

        const lastMessages$ = collectionData<TableMessage>(
            this.gameService.getLastMessages(gameID),
            { idField: 'messageID' }
        );

        const filteredPlayerIds$ = this.ProfileStore.getMutedInChatPlayerIdsObservable();

        const lastTableMessages = await firstValueFrom(combineLatest(lastMessages$, filteredPlayerIds$)
            .pipe(
                filter((data) => data != null),
                map(([messages, muted]) => messages.filter(message => !muted.includes(message.senderID))),
            )
        )

        if (lastTableMessages.length !== 0) {
            this.store.dispatch(GameActions.GameDispatcherActions.tableMessageDataUpdated({messages: lastTableMessages, gameStateId, playerId}));
        }

        const lastMessageSentAt = lastTableMessages[0] ? lastTableMessages[0].dateCreated.toDate() : new Date();

        this.detachTableMessageListener[gameStateId] = onSnapshot(this.gameService.getTableMessages(gameID, lastMessageSentAt), (snapshot) => {
            snapshot.docChanges().forEach(async (change) => {
                if (change.type === "added") {
                    const newMessage = {
                        ...change.doc.data(),
                        'messageID': change.doc.id,
                    };
                    const mutedPlayers = await firstValueFrom(filteredPlayerIds$);
                    if (mutedPlayers.includes(newMessage.senderID)) return;
                    this.store.dispatch(GameActions.GameDispatcherActions.newTableMessageRecieved({message: newMessage, gameStateId, playerId}));
                }
            });
        });
    }

    getTableMessageCount(gameStateId: GameStateId): Observable<number> {
        return this.getState().pipe(
            filter((gameState) => gameState.games[gameStateId] != null),
            map((game) => game.games[gameStateId].unreadMessageCount),
            distinctUntilChanged(),
        );
    }
    resetTableMessageCount(gameStateId: GameStateId) {
        this.store.dispatch(GameActions.GameDispatcherActions.resetUnreadTableMessagesCount({gameStateId}));
    }

    /** Get table messages */
    getTableMessages(gameStateId: GameStateId): Observable<TableMessage[]> {
        return this.getState().pipe(
            filter((gameState) => gameState.games[gameStateId] != null),
            map((game) => game.games[gameStateId].messages),
            distinctUntilChanged(),
        );
    }

    resetDealer(tableID) {
        this.getGameStateId(tableID).then((id) => {
            if (id != null) this.store.dispatch(GameActions.dealerResetAction({gameStateId: id}));
        });
    }

    resetDealerByGameStateId(gameStateId: GameStateId) {
        this.store.dispatch(GameActions.dealerResetAction({gameStateId: gameStateId}));
    }

    getGameStateId(tableID: string): Promise<GameStateId | null> {
        return firstValueFrom(this.getState()
            .pipe(
                map((gameState) => gameState.gamesList),
                map((games) => {
                    const game = games.filter(g => g.componentType === 'game').find((g: GameToLoad) => g.tableId === tableID)
                    if (game) return game.gameStateId;
                    return null;
                }),
            ));
    }

    public toggleStats() {
        this.store.dispatch(GameSettingsAction.toggleStatsAction());
    }

    public loadGame(gameID: string, tournamentId: string, tournamentType: string, gameStateId: GameStateId, userID: string): void {
        if (gameID != null) {
            //this.audioService.playSounds('join');
            if (this.tableSubscription[gameStateId]) {
                this.tableSubscription[gameStateId].unsubscribe();
            }

            if (this.tournamentSubscription[gameStateId]) {
                this.tournamentSubscription[gameStateId].unsubscribe();
            }

            if (this.audioSubscription[gameStateId]) {
                this.audioSubscription[gameStateId].unsubscribe();
            }

            if (this.timebankSubscription) {
                this.timebankSubscription.unsubscribe();
            }

            const timebankDocRef = doc(this.firestore, `users/${userID}/timebanks/timebank`);
            this.timebankSubscription = docData(timebankDocRef)
                .pipe(
                    filter(data => data != undefined && data != null),
                    takeUntil(this.fireSubService.unsubscriber)
                )
                .subscribe((snapshot: any) => {
                    if (snapshot.timebank_count != undefined && snapshot.timebank_count != null) {
                        this.store.dispatch(GameActions.GameDispatcherActions.timebankCountUpdated({count: snapshot.timebank_count}));
                    }
                });

            if (tournamentType == TableType.RING) {
                const docReference = doc(this.firestore, `/tables/${gameID}`);
                this.tableSubscription[gameStateId] = docData(docReference)
                    .pipe(filter((data) => data != null), takeUntil(this.fireSubService.unsubscriber))
                    .subscribe((snapshot: ITableState) => {
                        this.store.dispatch(GameActions.GameDispatcherActions.tableDataUpdated({table: snapshot, gameStateId}));
                    });

            } else {
                if (tournamentId !== undefined) {
                    const docReference = doc(this.firestore, `tournaments/${tournamentId}`);
                    this.tournamentSubscription[gameStateId] = docData(docReference)
                        .pipe(filter((data) => data != null), takeUntil(this.fireSubService.unsubscriber))
                        .subscribe((snapshot: ITournamentState) => {
                            if (snapshot) {
                                this.store.dispatch(GameActions.GameDispatcherActions.tournamentDataUpdated({tournament: snapshot, gameStateId}));
                            }
                        });
                    const tableDocRef = doc(this.firestore, `tournaments/${tournamentId}/tables/${gameID}`);
                    this.tableSubscription[gameStateId] = docData(tableDocRef)
                        .pipe(filter((data) => data !== null), takeUntil(this.fireSubService.unsubscriber))
                        .subscribe((data: ITableState) => {
                            this.store.dispatch(GameActions.GameDispatcherActions.tableDataUpdated({table: data, gameStateId}));
                            this.store.dispatch(GameActions.GameDispatcherActions.tournamentTablesDataUpdated({tables: {[data.id]: data}, gameStateId}));
                        })
                }
            }
            const selectedGame$ = this.getState()
            .pipe(pluck('selectedGame'));

            this.audioSubscription[gameStateId] = this.getDealer(gameStateId)
            .pipe(
                withLatestFrom(selectedGame$),
                filter(([dealer,selectedGame]) => selectedGame == gameStateId),
                map(([dealer,selectedGame]) => dealer)
            ).subscribe((dealer) => {
                const lastAction = dealer['lastAction'];
                switch (lastAction) {
                    case GameEventType.CALL:
                    case GameEventType.BET:
                    case GameEventType.POST_ANTE:
                    case GameEventType.POST_BLINDS:
                    case GameEventType.POST_BOMB_POT:
                        this.playSoundsInGame('bet-call')
                        break;
                    case GameEventType.RAISE:
                        this.playSoundsInGame('wager');
                        break;
                    case GameEventType.ALL_IN:
                        this.playSoundsInGame('all-in');
                    case GameEventType.CHECK:
                    case GameEventType.CHECK_BY_TIMEOUT:
                        this.playSoundsInGame('check');
                        break;
                    case GameEventType.FOLD:
                        this.playSoundsInGame('fold');
                        break;
                    case GameEventType.ADVANCE_TO_FLOP:
                    case GameEventType.ADVANCE_TO_FLOP_MULTIPLE:
                    case GameEventType.ADVANCE_TO_TURN:
                    case GameEventType.ADVANCE_TO_TURN_MULTIPLE:
                    case GameEventType.ADVANCE_TO_RIVER:
                    case GameEventType.ADVANCE_TO_RIVER_MULTIPLE:
                        this.playSoundsInGame('community-card');
                        break;
                    case GameEventType.ADVANCE_TO_LAST_MAN_STANDING:
                    case GameEventType.ADVANCE_TO_THIRD_STREET:
                    case GameEventType.ADVANCE_TO_FOURTH_STREET:
                    case GameEventType.ADVANCE_TO_FIFTH_STREET:
                    case GameEventType.ADVANCE_TO_SIXTH_STREET:
                    case GameEventType.ADVANCE_TO_SEVENTH_STREET:
                        this.playSoundsInGame('fold');
                        break;
                    case GameEventType.ADVANCE_TO_WINNER:
                    case GameEventType.ADVANCE_TO_WINNER_MULTIPLE_FIRST:
                    case GameEventType.ADVANCE_TO_WINNER_MULTIPLE_SECOND:
                    case GameEventType.ADVANCE_TO_WINNER_MULTIPLE_THIRD:
                    case GameEventType.ADVANCE_TO_WINNER_MULTIPLE_FOURTH:
                        this.playSoundsInGame('win');
                        break;
                    case GameEventType.LOAD_GAME:
                        // this.playSoundsInGame('shuffle');
                        dealer.playersInPlay.forEach((player) => {
                            this.playSoundsInGame('fold');
                        });
                        localStorage.setItem(`${gameID}.topUpAdded`, '0');
                        break;
                    default:
                        break;
                }
            });
        } else {
            console.log(`Dispatcher tried to load game but gameID was: ${gameID}`);
        }
    }

    public loadGameState(dealer: IGameState, playerId: string, gameStateId: GameStateId) {
        if (dealer == null) {
            console.error(`Cannot find game state`);
            return;
        }
        const lastAction = dealer['lastAction'];
        switch (lastAction) {
            case GameEventType.POST_BLINDS:
                this.store.dispatch(GameActions.GameDispatcherActions.blindsPosted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.POST_ANTE:
                this.store.dispatch(GameActions.GameDispatcherActions.antePosted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.POST_BLINDS_AND_ANTE:
                this.store.dispatch(GameActions.GameDispatcherActions.blindsAndAntePosted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.POST_DEAD_BLINDS:
                this.store.dispatch(GameActions.GameDispatcherActions.postDeadBlinds({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.LOAD_GAME:
                this.store.dispatch(GameActions.GameDispatcherActions.gameLoaded({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_HOLDEM:
                this.store.dispatch(GameActions.GameDispatcherActions.holdEmGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_SHORTDECK:
                this.store.dispatch(GameActions.GameDispatcherActions.shortDeckGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_OMAHA:
                this.store.dispatch(GameActions.GameDispatcherActions.omahaGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_OMAHA5:
                this.store.dispatch(GameActions.GameDispatcherActions.omaha5GameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_OMAHA_HI_LO:
                this.store.dispatch(GameActions.GameDispatcherActions.omahaHiLoGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_RAZZ:
                this.store.dispatch(GameActions.GameDispatcherActions.razzGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_STUD:
                this.store.dispatch(GameActions.GameDispatcherActions.studGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_STUD_HI_LO:
                this.store.dispatch(GameActions.GameDispatcherActions.studHiLoGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADD_TIME:
                this.store.dispatch(GameActions.GameDispatcherActions.playerAddedTime({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.BET:
                this.store.dispatch(GameActions.GameDispatcherActions.playerBetted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.CHECK:
                this.store.dispatch(GameActions.GameDispatcherActions.playerChecked({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.CALL:
                this.store.dispatch(GameActions.GameDispatcherActions.playerCalled({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.FOLD:
                this.store.dispatch(GameActions.GameDispatcherActions.playerFolded({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.FOLD_NO_SHOWDOWN:
                this.store.dispatch(GameActions.GameDispatcherActions.playerFoldedNoShowdown({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.HUNT_RABBIT:
                this.store.dispatch(GameActions.GameDispatcherActions.playerHuntedRabbit({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.RAISE:
                this.store.dispatch(GameActions.GameDispatcherActions.playerRaised({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.CHECK_BY_TIMEOUT:
                this.store.dispatch(GameActions.GameDispatcherActions.playerCheckedByTimeout({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.FOLD_BY_TIMEOUT:
                this.store.dispatch(GameActions.GameDispatcherActions.playerFoldedByTimeout({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ALL_IN:
                this.store.dispatch(GameActions.GameDispatcherActions.playerWentAllIn({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_LAST_MAN_STANDING:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToLastManStanding({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_FLOP:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToFlop({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_TURN:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToTurn({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_RIVER:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToRiver({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_THIRD_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToThirdStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_FOURTH_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToFourthStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_FIFTH_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToFifthStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_SIXTH_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToSixthStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_SEVENTH_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToSeventhStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_SHOWDOWN:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToShowdown({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_WINNER:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToPayWinner({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_BAD_BEAT_JACKPOT:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToBadBeatJackpot({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_WINNER_MULTIPLE_FIRST:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToPayWinner({game: dealer, playerId, gameStateId}));
                break;
            default:
                this.store.dispatch(GameActions.GameDispatcherActions.dealerDataUpdated({game: dealer, playerId, gameStateId}));
                break;
        }
    }

    public updateDeltaGameState(dealer: IStrippedGameState, playerId: string, gameStateId: GameStateId) {
        const lastAction = dealer['lastAction'];
        switch (lastAction) {
            case GameEventType.LOAD_GAME:
                this.store.dispatch(GameActions.GameDispatcherActions.gameLoaded({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_HOLDEM:
                this.store.dispatch(GameActions.GameDispatcherActions.holdEmGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_SHORTDECK:
                this.store.dispatch(GameActions.GameDispatcherActions.shortDeckGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_OMAHA:
                this.store.dispatch(GameActions.GameDispatcherActions.omahaGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_OMAHA5:
                this.store.dispatch(GameActions.GameDispatcherActions.omaha5GameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_OMAHA_HI_LO:
                this.store.dispatch(GameActions.GameDispatcherActions.omahaHiLoGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_RAZZ:
                this.store.dispatch(GameActions.GameDispatcherActions.razzGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_STUD:
                this.store.dispatch(GameActions.GameDispatcherActions.studGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.START_STUD_HI_LO:
                this.store.dispatch(GameActions.GameDispatcherActions.studHiLoGameStarted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADD_TIME:
                this.store.dispatch(GameActions.GameDispatcherActions.playerAddedTime({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.BET:
                this.store.dispatch(GameActions.GameDispatcherActions.playerBetted({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.CHECK:
                this.store.dispatch(GameActions.GameDispatcherActions.playerChecked({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.CALL:
                this.store.dispatch(GameActions.GameDispatcherActions.playerCalled({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.FOLD:
                this.store.dispatch(GameActions.GameDispatcherActions.playerFolded({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.FOLD_NO_SHOWDOWN:
                this.store.dispatch(GameActions.GameDispatcherActions.playerFoldedNoShowdown({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.HUNT_RABBIT:
                this.store.dispatch(GameActions.GameDispatcherActions.playerHuntedRabbit({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.RAISE:
                this.store.dispatch(GameActions.GameDispatcherActions.playerRaised({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.CHECK_BY_TIMEOUT:
                this.store.dispatch(GameActions.GameDispatcherActions.playerCheckedByTimeout({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.FOLD_BY_TIMEOUT:
                this.store.dispatch(GameActions.GameDispatcherActions.playerFoldedByTimeout({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ALL_IN:
                this.store.dispatch(GameActions.GameDispatcherActions.playerWentAllIn({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_LAST_MAN_STANDING:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToLastManStanding({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_FLOP:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToFlop({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_TURN:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToTurn({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_RIVER:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToRiver({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_THIRD_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToThirdStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_FOURTH_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToFourthStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_FIFTH_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToFifthStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_SIXTH_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToSixthStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_SEVENTH_STREET:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToSeventhStreet({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_SHOWDOWN:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToShowdown({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_WINNER:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToPayWinner({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_BAD_BEAT_JACKPOT:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToBadBeatJackpot({game: dealer, playerId, gameStateId}));
                break;
            case GameEventType.ADVANCE_TO_GAMEOVER:
                this.store.dispatch(GameActions.GameDispatcherActions.advancedToGameOver({game: dealer, playerId, gameStateId}));
                break;
            default:
                this.store.dispatch(GameActions.GameDispatcherActions.updateDeltaGameState({game: dealer, playerId, gameStateId}));
                break;
        }
    }

    public loadHandState(hand: any, playerId: string, gameStateId: GameStateId) {
        if (hand == null) {
            console.error(`Cannot find game state`);
            return;
        }
        this.store.dispatch(GameActions.GameDispatcherActions.handDataUpdated({hand, playerId, gameStateId}));
    }

    public getPlayersInPlay(gameStateId: GameStateId): Observable<PlayerID[]> {
        return this.getDealer(gameStateId).pipe(pluck('playersInPlay'));
    }

    public isGameOver(gameStateId: GameStateId): Observable<boolean> {
        return this.getDealer(gameStateId).pipe(pluck('gameOver'));
    }

    public isMyTurn(gameStateId: GameStateId): Observable<boolean> {
        const userId$ = this.store.select(state => state.auth).pipe(
            filter(auth => auth != null && auth.userData != null),
            map(auth => auth.userData.uid),
        );

        return this.getDealer(gameStateId).pipe(
            pluck('bettingQueue'),
            filter((queue) => queue != null),
            distinctUntilChanged(),
            switchMap((queue) => {
                return userId$.pipe(
                    map((uid) => {
                        const firstToAct = queue[0] === uid;
                        return firstToAct;
                    }),
                );
            }),
        );
    }

    public isPlayersTurn(id: string, gameStateId: GameStateId): Observable<boolean> {
        return this.getDealer(gameStateId).pipe(
            pluck('bettingQueue'),
            filter((queue) => queue != null),
            distinctUntilChanged(),
            map((queue) => {
                const firstToAct = queue[0] === id;
                return firstToAct;
            }),
        );
    }

    public isPlayersTurnNotLastMan(id: string, gameStateId: GameStateId): Observable<boolean> {
        return this.getDealer(gameStateId).pipe(
            pluck('bettingQueue'),
            filter((queue) => queue != null),
            distinctUntilChanged(),
            switchMap((queue) => {
                return this.getDealer(gameStateId).pipe(
                    pluck('playersInPlay'),
                    map((playersInPlay) => {
                        const firstToAct = queue[0] === id && playersInPlay.length >= 2;
                        return firstToAct;
                    }),
                );
            }),
        );
    }

    // public amIInPlay(): Observable < boolean > {
    //     const getMyUserID$: Observable < string > = this.profileService.getMyUserID();
    //     return getMyUserID$.pipe(
    //         switchMap(id => {
    //             return this.getDealer().pipe(
    //                 pluck('players', `${id}`, 'isInPlay')
    //             );
    //         })
    //     );
    // }

    /** Returns an Observable of non-null GameStates */
    public getState(): Observable<GameState> {
        return this.store.select((state: AppState) => state.game).pipe(filter((gameState) => gameState != null));
    }

    public getShowStatsUI(): Observable<string | null> {
        return this.store.select((state: AppState) => state.gameSettings).pipe(pluck('showStatsUI'));
    }

    public isTimeToShow(gameStateId: GameStateId): Observable<boolean> {
        return this.getDealer(gameStateId).pipe(
            map((dealer) => {
                return dealer.status === 'SHOWDOWN' && dealer.playersInPlay.length >= 2;
            }),
        );
    }

    public isTimeToPayWinner(gameStateId: GameStateId): Observable<boolean> {
        return this.getDealer(gameStateId).pipe(
            map((dealer) => {
                return (
                    dealer.status === 'SHOWDOWN' || dealer.status === 'PAY WINNER' || dealer.status === 'REVEAL_CARDS'
                );
            }),
        );
    }

    /** Returns an Observable of the non-null Dealer object  */
    public getDealer(gameStateId: GameStateId): Observable<IGameState> {
        return this.getState().pipe(
            filter((gameState) => gameState.games[gameStateId] != null),
            map((game) => game.games[gameStateId].dealer),
            filter((dealer) => Object.keys(dealer).length != 0),
            distinctUntilChanged(),
        );
    }

    public getTimebankCount(): Observable<number> {
        return this.getState().pipe(
            map((game) => game.timebankCount),
            distinctUntilChanged(),
        );
    }

    public getGameSettings (gameStateId: GameStateId): Observable<GameSettings | undefined> {
        return this.getState().pipe(
            filter((gameState) => gameState.gamesList[gameStateId] != undefined),
            map((game) => game.gamesList[gameStateId].settings),
            filter(settings => settings != undefined),
            distinctUntilChanged(),
        );
    }

    public getCommunityCards(gameStateId: GameStateId): Observable<string[]> {
        const compareArrayLengths = (arr1: string[], arr2: string[]) => {
            return arr1.length === arr2.length;
        };

        return this.getDealer(gameStateId).pipe(pluck('communityCards'), distinctUntilChanged(compareArrayLengths));
    }

    public getRunMultipleCards(gameStateId: GameStateId): Observable<[string, string[]][]> {
        const compareArrayLengths = (arr1: [string, string[]][], arr2: [string, string[]][]) => {
            if (arr1.length !== arr2.length) {
                return false;
            }
            let notChanged: boolean = true;
            arr1.forEach((element, index) => {
                if (arr1[index][1].length !== arr2[index][1].length) {
                    notChanged = false;
                    return false;
                }
            });
            return notChanged;
        };
        return this.getDealer(gameStateId).pipe(
            pluck('communityCardsMultiple'),
            map(cards => Object.entries(cards)),
            map(cards => cards.reverse()),
            distinctUntilChanged(compareArrayLengths)
        )
    }

    public getRabbitHuntCommunityCards(gameStateId: GameStateId): Observable<string[]> {
        const compareArrayLengths = (arr1: string[], arr2: string[]) => {
            return arr1.length === arr2.length;
        };
        return this.getDealer(gameStateId).pipe(
            map(dealer => dealer?.huntRabbitCommunityCards), 
            filter(cards => cards !== null && cards !== undefined),
            distinctUntilChanged(compareArrayLengths),
        );
    }

    public pluckFromDealer(pluckID: string, gameStateId: GameStateId): Observable<any> {
        return this.getDealer(gameStateId).pipe(
            pluck(pluckID),
            filter((obj) => obj != null),
        );
    }

    // public getGameType(): Observable < GameType > {
    public getGameType(gameStateId: GameStateId): Observable<GameType> {
        return this.getDealer(gameStateId).pipe(pluck('options', 'gameType'));
    }

    public getLimitType(gameStateId: GameStateId): Observable<LimitType> {
        return this.getDealer(gameStateId).pipe(pluck('options', 'limitType'));
    }

    public getPlayersWhoStartedTheGame(gameStateId: GameStateId): Observable<any> {
        return this.getDealer(gameStateId).pipe(pluck('seed', 'playersWhoStartedTheGame'));
    }

    public getUnfilteredTable(gameStateId: GameStateId) {
        return this.getState().pipe(
            filter((gameState) => gameState.games[gameStateId] != null),
            map((gameState) => gameState.games[gameStateId].table),
            filter(table => table !== undefined),
            distinctUntilChanged(),
        );
    }

    /** Returns an Observable of the non-null Table object  */
    public getTable(gameStateId: GameStateId): Observable<ITableState> {
        return this.getUnfilteredTable(gameStateId).pipe(
            filter((table) => table.id !== ''),
        );
    }

    /** Returns an Observable of the non-null PlayerStack object  */
    public getPlayerStack(gameStateId: GameStateId): Observable<IPlayerDictionary> {
        return this.getState().pipe(
            filter((gameState) => gameState.games[gameStateId] !== undefined),
            filter((gameState) => gameState.games[gameStateId].players != null),
            distinctUntilChanged(),
            map((gameState) => gameState.games[gameStateId].players),
        );
    }

    public async getTableData(gameStateId: GameStateId): Promise<Game> {
        const tableState = await firstValueFrom(this.getState()
            .pipe(
                filter((gameState) => gameState.games[gameStateId] != null),
                map((gameState) => gameState.games[gameStateId]),
                filter((game) => Object.keys(game.table).length != 0),
            ));
        return tableState;
    }

    public getTournament(gameStateId: GameStateId): Observable<ITournamentState> {
        return this.getState().pipe(
            filter((gameState) => gameState.games[gameStateId] != null),
            map((gameState) => gameState.games[gameStateId].tournament),
            filter((tournament) => tournament.clubID !== ''),
            distinctUntilChanged(),
        );
    }

    /**
    * Returns a promise of a string of the current club ID of a table
    */
    public async getClubId(gameStateId: GameStateId): Promise<string> {
        const clubId = await firstValueFrom(this.getTable(gameStateId)
        .pipe(
            filter((table) => table.clubID != null),
            map((table) => table.clubID),
        ));
        if (clubId === undefined) return Promise.reject();
        return Promise.resolve(clubId);
    }

    /**
    * Returns a promise of a string of the current club ID of a tournament
    */
    public async getTournamentClubId(gameStateId: GameStateId): Promise<string> {
        const clubId = await firstValueFrom(this.getTournament(gameStateId)
        .pipe(
            filter((tournament) => tournament.clubID != null),
            map((tornament) => tornament.clubID),
        ));
        if (clubId === undefined) return Promise.reject();
        return Promise.resolve(clubId);
    }

    /**
     * Returns a promise of a string of the current table's ID
     */
    public async getTableID(gameStateId: GameStateId): Promise<string> {
        const tableId = await firstValueFrom(this.getTable(gameStateId)
            .pipe(
                filter((table) => table.id != null),
                map((table) => table.id),
            ));
        if (tableId === undefined) return Promise.reject();
        return Promise.resolve(tableId);
    }

    getGameInfo(gameStateId: GameStateId): Promise<GameToLoad | undefined> {
        return firstValueFrom(this.getState()
        .pipe(
            map(state => {
                const game = state.gamesList.find(game => game.componentType === 'game' && game.gameStateId === gameStateId);
                return game as GameToLoad | undefined;
            }),
        ));
    }

    /**
     * Returns a promise of a string of the current table's Status
     */
    public async getTableStatus(gameStateId: GameStateId): Promise<string | undefined> {
        return firstValueFrom(this.getTable(gameStateId)
            .pipe(
                filter((table) => table.status != null),
                map((table) => table.status),
            ));
    }

    public getTableServer(gameStateId: GameStateId) {
        return this.getTable(gameStateId).pipe(
            map(table => table.server),
            distinctUntilChanged((prev, next) => prev.state === next.state && prev.gameServerName === next.gameServerName),
        )
    }

    /**
     * Returns a promise of a boolean indicating if the current table is a club table.
     * (Lobby tables have a clubID === SOPO)
     */
    public async isClubTable(gameStateId: GameStateId): Promise<boolean> {
        return firstValueFrom(this.getTable(gameStateId)
            .pipe(
                filter((table) => table.id != null),
                map((table) => table.clubID !== 'SOPO'),
            ));
    }

    public async isClubTournament(gameStateId: GameStateId): Promise<boolean> {
        return firstValueFrom(this.getTournament(gameStateId)
            .pipe(
                filter((tournament) => tournament.id != null),
                map((tournament) => tournament.clubID !== 'SOPO'),
            ));
    }

    public async getClientIsSeated(gameStateId: GameStateId): Promise<Observable<boolean>> {
        return this.ProfileStore.getMyID()
            .then((id) => {
                const clientID = id;
                if (clientID == null) {
                    return of(false);
                } else {
                    return this.getUnfilteredTable(gameStateId).pipe(
                        pluck('seats'),
                        map((seats: string[]) => {
                            return seats && seats.indexOf(clientID) > -1;
                        }),
                    );
                }
            })
            .catch((error) => Promise.reject(of(false)));
    }

    public isClientSeated(clientID: string, gameStateId: GameStateId): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if (clientID == null) {
                return reject(false);
            } else {
                firstValueFrom(this.getTable(gameStateId)
                    .pipe(pluck('seats')))
                    .then((seats) => {
                        const returnValue: boolean = (seats && seats.indexOf(clientID) > -1) as boolean
                        resolve(returnValue);
                    });
            }
        });
    }

    public setNewClubId(gameStateId: GameStateId, clubId: string): void {
        return this.store.dispatch(GameActions.GameDispatcherActions.changeClubId({gameStateId, clubId}));
    }

    public setTableBottomImage(gameStateId: string, imageSrc: string) {
        this.store.dispatch(GameActions.tableBottomImageSet({ gameStateId, imageSrc }));
    }

    public getTableBottomImage(gameStateId: string): Observable<string> {
        return this.getState().pipe(
            filter((gameState) => gameState.games[gameStateId] !== undefined),
            filter((gameState) => gameState.games[gameStateId].tableBottomAreaImage !== null),
            map(gameState => { return gameState.games[gameStateId].tableBottomAreaImage }), distinctUntilChanged()
        )
    }

    public getTableIsDeleted(gameStateId: GameStateId): Observable<boolean> {
        return this.getState()
            .pipe(
                filter((gameState) => gameState.games[gameStateId] != null),
                map((gameState) => gameState.games[gameStateId]),
                map((gameState) => gameState.table.deleteTable)
            )
    }

    public getTournamentIsDeleted(gameStateId: GameStateId): Observable<boolean> {
        return this.getState()
            .pipe(
                filter((gameState) => gameState.games[gameStateId] != null),
                map((gameState) => gameState.games[gameStateId]),
                map((gameState) => gameState.tournament.isDeleted),
            )
    }

    public getGameIsDeleted(gameStateId: GameStateId): Observable<boolean> {
        const tableIsDeleted$ = this.getTableIsDeleted(gameStateId);
        const tournamentIsDeleted$ = this.getTournamentIsDeleted(gameStateId);

        return merge(tableIsDeleted$, tournamentIsDeleted$).pipe(distinctUntilChanged());
    }

    public resetActions() {
        this.store.dispatch(GameActions.GameDispatcherActions.timebankCountReset());
        this.store.dispatch(EmotesActions.emotesCountResetAction());
    }

    public leaveGame(gameStateId: GameStateId) {
        if (this.tableSubscription[gameStateId]) {
            this.tableSubscription[gameStateId].unsubscribe();
        }
        if (this.detachTableMessageListener[gameStateId]) {
            this.detachTableMessageListener[gameStateId]();
        }
        if (this.tournamentSubscription[gameStateId]) {
            this.tournamentSubscription[gameStateId].unsubscribe();
        }
        if (this.audioSubscription[gameStateId]) {
            this.audioSubscription[gameStateId].unsubscribe();
        }
    }

    async loadGamesFromStorage(gameId: GameStateId) {
        const queryString = localStorage.getItem(`gameInfo-${gameId}`);
        if (queryString === null) return;

        const games: GameToLoad[] = [JSON.parse(queryString)];

        for (let gameToLoad of games) {
            this.addNewGame(gameToLoad, false, true);
        }

        const gridViewEnabled = localStorage.getItem('gridView');

        if (gridViewEnabled) {
            this.setGridView(JSON.parse(gridViewEnabled));
        }
    }

    public updateGameToPreviewId(gameToPreviewId: string) {
        this.store.dispatch(GameActions.GameDispatcherActions.updateGameToPreviewId({gameToPreviewId}));
    }

    public playSoundsInGame(action: Sound){
        if (this.isInGame) {
            console.debug(`[SOUND] - playSoundsInGame - ${action}`);
            this.audioService.playSounds(action);
        }
    }
}
